import { useEffect, useState } from "react";

import { api, errorMessage } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import StatusMessage, {
  errorStatus,
  successStatus,
} from "../StatusMessage/StatusMessage";
import { appUrl, shareOrCopy, shareResultMessage } from "../utils/share";

function FamilyCheckBox({ family, isChecked, onChange }) {
  return (
    <div>
      <label htmlFor={`id_families_${family.family_guid}`}>
        <input
          type="checkbox"
          name="families"
          value={family.family_guid}
          id={`id_families_${family.family_guid}`}
          checked={isChecked}
          onChange={(e) => onChange(family.family_guid, e.target.checked)}
        ></input>
        {family.surname}
      </label>
    </div>
  );
}

/**
 * Create an invite link for the families the member can invite to. The link
 * opens the public /join page, where the new member signs up.
 */
export default function InviteNewMemberForm() {
  const { session } = useAuth();
  const [families, setFamilies] = useState(null);
  const [selectedFamilies, setSelectedFamilies] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [inviteUrl, setInviteUrl] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    api
      .get("/family/api/families/invite/", session.token)
      .then(setFamilies)
      .catch((err) =>
        setMessage(
          errorStatus(`Couldn't load your families: ${errorMessage(err)}`),
        ),
      );
  }, [session.token]);

  const handleFamilyCheckboxChange = (familyGuid, isChecked) => {
    setSelectedFamilies((prev) =>
      isChecked
        ? prev.includes(familyGuid)
          ? prev
          : [...prev, familyGuid]
        : prev.filter((guid) => guid !== familyGuid),
    );
  };

  const handleCreateInviteCode = async () => {
    setIsCreating(true);
    setMessage(null);
    let url;
    try {
      const inviteCode = await api.post("/family/api/invite_code/", session.token, {
        families: selectedFamilies.map((guid) => ({ family_guid: guid })),
      });
      url = appUrl(`/join/${inviteCode.invite_code}`);
      setInviteUrl(url);
    } catch (err) {
      setMessage(errorStatus(`Couldn't create the invite: ${errorMessage(err)}`));
      return;
    } finally {
      setIsCreating(false);
    }

    const result = await shareOrCopy({
      url,
      title: "Join the family",
      text: "Join my family!",
    });
    const text = shareResultMessage(result);
    if (text) setMessage(successStatus(text));
  };

  if (!families) {
    return message ? (
      <div className="grid-item">
        <StatusMessage message={message} />
      </div>
    ) : null;
  }

  if (families.length === 0) {
    return (
      <div className="grid-item">
        <div className="edit-post-container">
          <h2>Grow the family!</h2>
          <p>You don't have permission to invite anyone to your families yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid-item">
      <div className="edit-post-container">
        <h2>Grow the family!</h2>
        <p>
          Select the families which you'd like to add the new member to.
          Selecting these families will automatically set which posts they can
          see. A member can see posts from other members of their families.
        </p>
        <br />
        {families.map((family) => (
          <FamilyCheckBox
            key={family.family_guid}
            family={family}
            isChecked={selectedFamilies.includes(family.family_guid)}
            onChange={handleFamilyCheckboxChange}
          />
        ))}
        <button
          type="button"
          className="interaction-button"
          onClick={handleCreateInviteCode}
          disabled={isCreating || selectedFamilies.length === 0}
        >
          {isCreating ? "Creating invite..." : "Grow your family!"}
        </button>
        <StatusMessage message={message} onDismiss={() => setMessage(null)} />
        {inviteUrl && (
          <>
            <p>Send this link to the new family member. It works once.</p>
            <input
              className="share-link-input"
              type="text"
              readOnly
              value={inviteUrl}
              aria-label="Invite link"
              onFocus={(e) => e.target.select()}
            />
          </>
        )}
      </div>
    </div>
  );
}
